import base64url from 'base64url';
import moment from 'moment';

import * as runtime from '../runtime';
import { EntityQueryResults, EntityQueryResultsFromJSON } from '../models/EntityQueryResults';
import { ApplicationQueryParams } from '../models/EntityQueries';
import { Application, ApplicationFromJSON, ApplicationToJSON } from '../models/Application';
import { ViewType } from '../../page/enums/ViewType';
import { UserRepresentationToJSON } from '../models/UserRepresentation';
import { BatchApplication, BatchApplicationFromJSON, BatchApplicationToJSON } from '../models/BatchApplication';
import { getCustomerVariables } from '../../features/context/CustomerContextHelper';
import { CustomerVariables } from '../../customerVariables/variable_interfaces';

export class ApplicationsApi extends runtime.BaseAPI {

    readonly customerVariables: CustomerVariables = getCustomerVariables();
    /**
     * Gets all applications
     */
    async getRepresentationApplicationsRaw(requestParameters: ApplicationQueryParams): Promise<runtime.ApiResponse<EntityQueryResults<Application>>> {
        const queryParameters: any = {};
        let applicationType = "";

        if (requestParameters.pageNumber !== undefined) {
            queryParameters['pageNumber'] = requestParameters.pageNumber
        }

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize
        }

        if (requestParameters.sort !== undefined && requestParameters.sort !== "") {
            queryParameters['sort'] = requestParameters.sort;
        }

        if (requestParameters.filter !== undefined && requestParameters.filter !== null && requestParameters.filterParam !== undefined && requestParameters.filterParam !== null) {
            if (requestParameters.filterParam instanceof Date) {
                const date = requestParameters.filterParam;
                date.setHours(0, 0, 0, 0);
                requestParameters.filterParam = moment(date).format('yyyy-MM-DDTHH:mm:ss+SSSS');
            }
            if (requestParameters.filter === 'statuses' && requestParameters.filterParam) {
                requestParameters.filterParam = requestParameters.filterParam.toUpperCase();
            }
            queryParameters[requestParameters.filter] = requestParameters.filterParam;
        }

        if (requestParameters.type === ViewType.RECEIVED_APPLICATIONS) {
            queryParameters['statuses'] = 'PENDING';
        }

        if (requestParameters.type === ViewType.SENT_APPLICATIONS) {
            applicationType = "sent"
        } else if (requestParameters.type === ViewType.RECEIVED_APPLICATIONS) {
            applicationType = "received"
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/realms/${this.customerVariables.realmName}/wapice/representationRequests/${applicationType}`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters
        });

        return new runtime.JSONApiResponse(response, (jsonValue) => EntityQueryResultsFromJSON(jsonValue, ApplicationFromJSON));

    }

    /**
     * Gets all applications
     */
    async getRepresentationApplications(requestParameters: ApplicationQueryParams): Promise<EntityQueryResults<Application>> {
        const response = await this.getRepresentationApplicationsRaw(requestParameters);
        return await response.value();
    }

    async updateRaw(application: Application): Promise<runtime.ApiResponse<Application>> {
        if (application === null || application === undefined) {
            throw new runtime.RequiredError('todo', 'Required parameter requestParameters.todo was null or undefined when calling update.');
        }

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/realms/${this.customerVariables.realmName}/wapice/representationRequests/`,
            method: 'PUT',
            headers: headerParameters,
            body: ApplicationToJSON(application)
        });

        return new runtime.JSONApiResponse(response, (jsonValue) => ApplicationFromJSON(jsonValue));
    }

    /**
     * Update application
     */
     async update(application: Application): Promise<Application>{
        const response = await this.updateRaw(application);
        return response.value();
     }

    async updateMultipleRaw(applications: BatchApplication[]): Promise<runtime.ApiResponse<BatchApplication[]>> {
        if (applications === null || applications === undefined) {
            throw new runtime.RequiredError('application', 'Required parameter application was null or undefined when calling update.');
        }
        
        let updatedList = applications?.map((individualApplication) => {
            individualApplication.requestorUser = UserRepresentationToJSON(individualApplication.requestorUser);
            individualApplication = BatchApplicationToJSON(individualApplication);
            return individualApplication;
        })

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';
        const response = await this.request({
            path: `/realms/${this.customerVariables.realmName}/wapice/representationRequests/batch`,
            method: 'PUT',
            headers: headerParameters,
            body: updatedList
        });

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(BatchApplicationFromJSON));
    }

    /**
    * Update multiple applications
    */
    async updateMultiple(applications: BatchApplication[]) {
        const response = await this.updateMultipleRaw(applications);
        return await response.value();
    }

    async delete(id: string | undefined) {
        await this.deleteRaw(id);
    }

    async deleteRaw(id: string | undefined): Promise<runtime.ApiResponse<void>> {
        if (id === null || id === undefined || id === '') {
            throw new runtime.RequiredError('id', 'Required parameter id was null or undefined when calling delete.');
        }

        const queryParameters: any = {};

        if (id && id.toString().includes("/")) {
            id = base64url.encode(id);
            queryParameters['urlPathParamEncoding'] = "base64url";
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/realms/${this.customerVariables.realmName}/wapice/representationRequests/${id}`,
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters
        })

        return new runtime.VoidApiResponse(response);
    }
}